import AddIcon from "@mui/icons-material/Add";
import React, { useEffect, useState } from "react";
import { IIdName } from "api/entities/id-name";
import { IPage } from "api/entities/page";
import {
  IShardRepository,
  IShardRepositoryFilterParams,
  IShardRepositoryRequestParams,
} from "api/entities/shard-repository";
import { shardRepositoryService } from "api/services/shard-repository-service";
import { shardClusterService } from "api/services/shard-cluster-service";
import {
  getDefaultRequestParams,
  getEmptyPage,
} from "../../redux/reducers/entity";
import { useApi } from "../../utils/hooks/use-api";
import { useFilters } from "../../utils/hooks/use-filter";
import { useNavigate } from "react-router-dom";
import { routeUtils } from "../../utils/route-utils";
import RefreshButton from "../shared-ui/button/refresh-button";
import IOption from "../shared-ui/common/option";
import { FiltersData } from "../shared-ui/filters-data/filters-data/filters-data";
import PageTitle from "../shared-ui/pageTitle";
import { ShardClusterAssociatedFilters } from "../common/sections/filters/shard-cluster-associated-filters";
import { DeleteBlockedConfirmation } from "./dialogs/delete-blocked-confirmation/delete-blocked-confirmation";
import { DeleteConfirmation } from "./dialogs/delete-confirmation/delete-confirmation";
import {
  defaultFilters,
  filterNames,
  searchBarStorageNodePoolTypeFilterTypeMap,
  StorageNodeTabFilters,
} from "./filters/filters";
import { ShardRepositoryTable } from "./shard-repository-table/shard-repository-table";
import styles from "./shard-repositories.module.scss";
import { storageTypeToNameMap } from "../../utils/storage-utils";
import ShardRepositoryContainedPanel from "./panels/shard-repository-contained-panel";

interface IShardRepositoryProps {}

function ShardRepositories(props: IShardRepositoryProps) {
  const [shardRepositoryApi, isShardRepositoryLoading] = useApi();
  const [shardClusterApi] = useApi();
  const [shardRepositoryPage, setShardRepositoryPage] =
    useState<IPage<IShardRepository>>(getEmptyPage());
  const [requestParams, setRequestParams] =
    useState<IShardRepositoryRequestParams>({
      ...getDefaultRequestParams(),
      deleted: false,
    });

  const [shardClusterOptions, setShardClusterOptions] = useState<
    IOption<string>[]
  >([]);

  const navigate = useNavigate();

  const [filters, setFilters, updateQuery] = useFilters(
    defaultFilters,
    requestData,
    searchBarStorageNodePoolTypeFilterTypeMap,
    filtersToQueryParams,
    queryParamsToFilters,
  );

  function filtersToQueryParams(
    filters: StorageNodeTabFilters,
  ): IShardRepositoryFilterParams {
    return {
      q: filters.searchText,
      pool: filters.shardClusterFilter?.value,
      storageType: filters.storageTypeFilter?.value,
    };
  }

  function queryParamsToFilters(
    queryParams: Partial<IShardRepositoryFilterParams>,
  ): StorageNodeTabFilters {
    return {
      searchText: queryParams.q,
      shardClusterFilter: queryParams.pool ? { value: queryParams.pool } : null,
      storageTypeFilter: queryParams.storageType
        ? {
            value: queryParams.storageType,
            label: storageTypeToNameMap[queryParams.storageType],
          }
        : null,
    };
  }

  function requestData(filters: StorageNodeTabFilters) {
    fetchData({
      ...filtersToQueryParams(filters),
      offset: 0,
    });
  }

  function fetchData(
    requestParamsUpdate: Partial<IShardRepositoryRequestParams>,
  ): Promise<number> {
    const updatedRequestParams = { ...requestParams, ...requestParamsUpdate };

    setRequestParams(updatedRequestParams);
    return shardRepositoryApi(
      shardRepositoryService.getPage(updatedRequestParams),
    ).then((page) => {
      setShardRepositoryPage(page);

      return page.count;
    });
  }

  function fetchStoragePoolOptionList() {
    shardClusterApi(shardClusterService.getPage({ fields: ["id", "name"] }))
      .then((page: IPage<IIdName<string>>) => {
        return page.data.map((storagePool) => ({
          value: storagePool.id,
          label: storagePool.name,
        }));
      })
      .then(setShardClusterOptions);
  }

  function handleAddStorageNodeFormPanelOpen() {
    navigate(routeUtils.getRouteToNewShardRepository());
  }

  function handleDeleteStorageNode(storageNodeId: string) {
    shardRepositoryApi(shardRepositoryService.delete(storageNodeId)).then(() =>
      fetchData({}),
    );
  }

  useEffect(() => {
    if (
      !!shardClusterOptions &&
      shardClusterOptions.length > 0 &&
      !!filters.shardClusterFilter &&
      !filters.shardClusterFilter.label
    ) {
      let storagePoolFilter = shardClusterOptions.find(
        (x) => x.value === filters.shardClusterFilter.value,
      );
      setFilters({ ...filters, shardClusterFilter: storagePoolFilter });
    }
    // eslint-disable-next-line
  }, [shardClusterOptions, filters.shardClusterFilter]);

  useEffect(() => {
    fetchStoragePoolOptionList();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <div className={styles.header}>
        <PageTitle title={`Shard Repositories`}>
          <RefreshButton onClick={() => fetchData(requestParams)} />
        </PageTitle>

        {/* <Button
                    className={styles.add}
                    responsive={true}
                    testId="new-storage-node-button"
                    colors="primary"
                    onClick={handleAddStorageNodeFormPanelOpen}
                >
                    <AddIcon />
                    <span className={styles["hidden-on-small-screen"]}>New Connection</span>
                    <span className={styles.add__md}>Add New</span>
                </Button> */}
      </div>

      <ShardClusterAssociatedFilters
        filters={filters}
        onFiltersChange={updateQuery}
        storagePoolOptions={shardClusterOptions}
      >
        {/* <FiltersBarItem>
                    <StorageTypeFilter
                        className={styles.provider}
                        value={filters.storageTypeFilter}
                        onChange={(storageType) => updateQuery({ ...filters, storageTypeFilter: storageType })}
                        autoWidth
                    />
                </FiltersBarItem> */}
      </ShardClusterAssociatedFilters>
      <FiltersData
        filterNames={filterNames}
        defaultFilters={defaultFilters}
        filters={filters}
        onFiltersChange={updateQuery}
      />

      <ShardRepositoryTable
        isLoading={isShardRepositoryLoading}
        page={shardRepositoryPage}
        requestParams={requestParams}
        setRequestParams={fetchData}
        hasManageColumn={true}
        isRowClickable={true}
      />

      <ShardRepositoryContainedPanel fetchStorageNodes={() => fetchData({})} />

      <DeleteConfirmation onDelete={handleDeleteStorageNode} />
      <DeleteBlockedConfirmation />
    </>
  );
}

export default ShardRepositories;
